import { Fragment } from "react/jsx-runtime";
import ConnectedBetBuildersCard from "../../../../BetBuildersCard";
import { BetBuildersCard } from "../../../../BetBuildersCard/BetBuildersCard.web";
import ConnectedMultiBetBuilderCard from "../../../../MultiBetBuilderCard";
import { MultiBetBuilderCard } from "../../../../MultiBetBuilderCard/MultiBetBuilderCard.web";
import ConnectedMultiLinesMultiples from "../../../../MultiLinesMultiples";
import { MultiLinesMultiples } from "../../../../MultiLinesMultiples/MultiLinesMultiples.web";
import ConnectedOneLineMultiple from "../../../../OneLineMultiple";
import { OneLineMultiple } from "../../../../OneLineMultiple/OneLineMultiple.web";
import ConnectedPriceBoostSection from "../../../../PriceBoostSection";
import { PriceBoostSection } from "../../../../PriceBoostSection/PriceBoostSection.web";
import ConnectedSinglesCard from "../../../../SinglesCard";
import { SinglesCard } from "../../../../SinglesCard/SinglesCard.web";
import ConnectedCastBetsCard from "../../../../CastBetsCard";
import { CastBetsCard } from "../../../../CastBetsCard/CastBetsCard.web";
import ConnectedLotteriesCard from "../../../../LotteriesCard";
import { LotteriesCard } from "../../../../LotteriesCard/LotteriesCard.web";
import UpsellSuggestions from "@ppb/tbd-components-flexible-betting-opportunities/components/UpsellSuggestions/view/UpsellSuggestions.web";
import { PebbleSection } from "./PebbleSection.web";
import { PebbleResolver, PebblesResolvers } from "./SportsbookPlacePanel.types";
import { getOrderedPebblesResolvers } from "./helpers/get-ordered-pebbles-resolver";
import { ConnectedBetBuilderFailure } from "../../../../BetBuilder";
import { BetBuilder } from "../../../../BetBuilder/BetBuilder.web";

const ALL_RESOLVER: PebbleResolver = {
  key: "all",
  title: (props) => props.i18n.tabAllTitle,
  visible: (props) => {
    const visiblePebbles = [BET_BUILDER_RESOLVER, MULTIPLES_RESOLVER, CAST_BET_RESOLVER, SINGLES_RESOLVER].filter(
      (resolver) => resolver.visible(props),
    );

    return visiblePebbles.length > 1;
  },
  content: (rawProps) => {
    // The "all" tab always shows several sections, so auto-focusing a stake field would scroll the betslip away from the top.
    const props = {
      ...rawProps,
      shouldFocusMultiple: false,
      shouldFocusBetBuilder: false,
      shouldFocusCastBet: false,
      hasOnlyOneSingle: false,
    };

    return (
      <>
        {getOrderedPebblesResolvers(
          {
            singles: SINGLES_RESOLVER,
            betBuilder: BET_BUILDER_RESOLVER,
            multiples: MULTIPLES_RESOLVER,
            castBet: CAST_BET_RESOLVER,
          },
          props.isSinglesFirst,
        )
          .filter((resolver) => resolver.visible(props))
          .map((resolver) => (
            <Fragment key={resolver.key}>{resolver.content(props)}</Fragment>
          ))}
      </>
    );
  },
};

const BET_BUILDER_RESOLVER: PebbleResolver = {
  key: "bet-builder",
  title: (props) => props.i18n.tabBetBuildersTitle,
  visible: (props) => props.hasMultiBetBuilder || props.hasBetBuilders,
  content: (props) => {
    return (
      <>
        {props.isUpsellSuggestionsEnabled && (
          <div style={{ marginInlineStart: "8px" }}>
            <UpsellSuggestions isDesktop={props.isDesktop} marketSelections={props.marketSelections} />
          </div>
        )}
        {props.hasMultiBetBuilder && (
          <PebbleSection
            card={
              <ConnectedMultiBetBuilderCard
                component={MultiBetBuilderCard}
                shouldFocusStakeField={props.shouldFocusBetBuilder}
              />
            }
            title={props.multiBetBuilderTitle}
          />
        )}
        {props.failedCombinationGroups?.length > 0 && (
          <PebbleSection
            card={
              <>
                {props.failedCombinationGroups.map((failedGroup) => (
                  <ConnectedBetBuilderFailure key={failedGroup} component={BetBuilder} combinationGroup={failedGroup} />
                ))}
              </>
            }
            title={props.i18n.betBuilder}
          />
        )}
        {props.hasBetBuilders &&
          props.betBuilderIds.map((id) => (
            <PebbleSection
              key={id}
              card={
                <ConnectedBetBuildersCard
                  component={BetBuildersCard}
                  combinationIds={[id]}
                  shouldFocusStakeField={props.shouldFocusBetBuilder}
                />
              }
              title={props.betBuilderTitles?.[id] ?? ""}
            />
          ))}
      </>
    );
  },
};

const MULTIPLES_RESOLVER: PebbleResolver = {
  key: "multiples",
  title: (props) => props.i18n.tabMultiplesTitle,
  visible: (props) => props.hasPriceBoost || props.hasOneLineMultiple || props.hasMultipleLinesMultiples,
  content: (props) => {
    return (
      <>
        {props.hasPriceBoost && (
          <PebbleSection
            card={
              <ConnectedPriceBoostSection
                component={PriceBoostSection}
                boostedCombinationIds={props.boostedCombinationIds}
                failedCombinationGroupIds={props.failedCombinationGroupIds}
                shouldFocusStakeField={props.shouldFocusMultiple}
              />
            }
            title={props.i18n.priceBoosts}
          />
        )}
        {props.hasOneLineMultiple && (
          <PebbleSection
            card={
              <ConnectedOneLineMultiple
                component={OneLineMultiple}
                hasOneLineMultiple={props.hasOneLineMultiple}
                shouldFocusStakeField={props.shouldFocusMultiple}
              />
            }
            title={props.oneLineMultipleTitle}
          />
        )}
        {props.hasMultipleLinesMultiples && (
          <PebbleSection
            card={<ConnectedMultiLinesMultiples component={MultiLinesMultiples} shouldFocusStakeField={false} />}
            title={props.i18n.additionalMultiples}
          />
        )}
      </>
    );
  },
};

const CAST_BET_RESOLVER: PebbleResolver = {
  key: "cast-bet",
  title: (props) => props.i18n.tabCastTitle,
  visible: (props) => props.hasCastBets,
  content: (props) => {
    return (
      <PebbleSection
        card={<ConnectedCastBetsCard component={CastBetsCard} shouldFocusStakeField={props.shouldFocusCastBet} />}
        title={props.i18n.casts}
      />
    );
  },
};

const SINGLES_RESOLVER: PebbleResolver = {
  key: "singles",
  title: (props) => props.i18n.tabSinglesTitle,
  visible: (props) => props.hasSingles,
  content: (props) => {
    const isQuickBetslipActive = !!props.isQuickBetslipActive;

    return (
      <PebbleSection
        card={
          <ConnectedSinglesCard
            component={SinglesCard}
            hasAvailabilityHints
            shouldFocusStakeField={props.hasOnlyOneSingle && !isQuickBetslipActive}
          />
        }
        title={props.i18n.singles}
      />
    );
  },
};

const LOTTERIES_RESOLVER: PebbleResolver = {
  key: "lotteries",
  title: (props) => props.i18n.tabLotteriesTitle,
  visible: (props) => props.hasLotteries,
  content: (props) => (
    <PebbleSection
      card={
        <ConnectedLotteriesCard
          component={LotteriesCard}
          hasAvailabilityHints
          shouldFocusStakeField={props.hasOnlyOneSingle}
        />
      }
      title={props.i18n.tabLotteriesTitle}
    />
  ),
};

export const PEBBLE_RESOLVERS: PebblesResolvers = {
  ALL: ALL_RESOLVER,
  MULTIPLES: MULTIPLES_RESOLVER,
  BET_BUILDER: BET_BUILDER_RESOLVER,
  CAST_BET: CAST_BET_RESOLVER,
  SINGLES: SINGLES_RESOLVER,
  LOTTERIES: LOTTERIES_RESOLVER,
};
